import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "er_token";
export const USER_KEY = "er_user";
export const REFRESH_KEY = "er_refresh"; // Phase 6a

const client = axios.create({
  baseURL: API_BASE,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let _refreshing = null;

async function tryRefresh() {
  if (_refreshing) return _refreshing;
  const refresh_token = localStorage.getItem(REFRESH_KEY);
  if (!refresh_token) return null;
  _refreshing = (async () => {
    try {
      const res = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      if (res.data.refresh_token) {
        localStorage.setItem(REFRESH_KEY, res.data.refresh_token);
      }
      return res.data.token;
    } catch {
      return null;
    } finally {
      _refreshing = null;
    }
  })();
  return _refreshing;
}

client.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config || {};
    const status = err.response?.status;
    if (
      status === 401 &&
      !originalRequest._retried &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !originalRequest.url?.includes("/auth/login") &&
      !originalRequest.url?.includes("/auth/mfa")
    ) {
      const newToken = await tryRefresh();
      if (newToken) {
        originalRequest._retried = true;
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${newToken}`,
        };
        return client(originalRequest);
      }
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(REFRESH_KEY);
      if (window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  // Phase 2 (May 2026) — bulk import returns a structured per-row error
  // payload of shape `{message, errors: [{row, reason}]}`. Render it inline
  // so the toast shows what to fix, not a useless "Something went wrong".
  if (
    detail &&
    typeof detail.message === "string" &&
    Array.isArray(detail.errors)
  ) {
    const top = detail.errors
      .slice(0, 3)
      .map((e) => `row ${e.row}: ${e.reason}`)
      .join(" · ");
    const rest = detail.errors.length > 3 ? ` (+${detail.errors.length - 3} more)` : "";
    return `${detail.message} ${top}${rest}`.trim();
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  // Structured error with a top-level `message` field but no `errors` list
  // (e.g. the service-line admin's EXISTS_INACTIVE / ACTIVE_CASES_ATTACHED
  // envelopes). Render the message so dialogs don't fall back to the
  // generic "[object Object]" string from the final `String(detail)` cast.
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

// Phase 2 (May 2026) — wider error extractor used by user-facing dialogs
// where a generic "Something went wrong" toast is unacceptable. Pulls the
// most specific signal out of an Axios error: the API `detail` field, then
// the HTTP status text, then the network/JS message, falling back only as a
// last resort.
export function describeApiError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail) return formatApiErrorDetail(detail);
  if (status === 408) return "The request timed out. Please try again.";
  if (status === 413) return "File too large. Please reduce row count.";
  if (status === 502 || status === 503 || status === 504) {
    return `Server unavailable (HTTP ${status}). Retry in a moment.`;
  }
  if (status === 500) {
    return "Internal server error. Check the import history or try again with a smaller file.";
  }
  if (status) return `Request failed with HTTP ${status}.`;
  if (err?.code === "ECONNABORTED") return "Request timed out. Please try again.";
  if (typeof err?.message === "string" && err.message) return err.message;
  return "Network error — check your connection and try again.";
}

export default client;
